import { Box } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import FilterBar from '@sophocles/components/FilterBar';
import ProductAutocomplete from '@sophocles/components/ProductAutocomplete';
import { EntityGrid } from '@sophocles/data-grids';
import useColumnVisibilityModel from '@sophocles/data-grids/datasource/useColumnVisibilityModel';
import { productInventoryLogSlice } from '@sophocles/features/warehouse/products/slice';
import { buildProductInventoryLogGridParams } from '@sophocles/views/sales/productInventoryLogGridParams';

/**
 * Sales > Products > Inventory Log.
 *
 * Audit log of stock movements (RECEIVE / PULL / ORDER / UPDATE). No
 * StatCards — no useful single-dimension segmentation on a log. Filters
 * are product autocomplete (existing) inline above the toolbar.
 *
 * Accepts a `?product=<id>` query param so the ProductDetailDrawer "View
 * all" link can deep-link into the log filtered to one product.
 */
export default function ProductInventoryLogGrid() {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = useSelector(state => productInventoryLogSlice.selectors.selectFilters(state));
  const columnVisibility = useColumnVisibilityModel('productInventoryLogGrid');

  // Seed the product filter from the URL exactly once per URL value, then
  // drop the param so the URL stays clean and Reset All can actually clear.
  const appliedSeedRef = useRef(null);
  useEffect(() => {
    const productFromUrl = searchParams.get('product');
    if (!productFromUrl) return;
    if (appliedSeedRef.current === productFromUrl) return;
    appliedSeedRef.current = productFromUrl;
    const productId = Number(productFromUrl);
    if (Number.isFinite(productId)) {
      dispatch(productInventoryLogSlice.actions.setFilters({ ...filters, product: productId }));
    }
    const next = new URLSearchParams(searchParams);
    next.delete('product');
    setSearchParams(next, { replace: true });
  }, [searchParams]);

  const extraParams = useMemo(() => buildProductInventoryLogGridParams(filters), [filters]);

  // Render overrides on top of descriptor column defaults. Sizing, order, and
  // headers come from the descriptor; overrides here are intentional display
  // decisions only.
  const fieldOverrides = useMemo(
    () => ({
      item_part_num: {
        renderCell: ({ value }) => <Box sx={{ fontFamily: 'monospace' }}>{value}</Box>,
      },
      qty: {
        renderCell: ({ value }) => <Box sx={{ fontFamily: 'monospace' }}>{value}</Box>,
      },
      // user_detail is the nested user object {id, first_name, last_name}; null
      // when the log entry has no associated user.
      user_detail: {
        valueGetter: (_value, row) => {
          const u = row.user_detail;
          return u ? `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim() : '';
        },
      },
      action_time: {
        valueFormatter: value => (value ? new Date(value).toLocaleString() : '-'),
      },
    }),
    [],
  );

  const handleProductChange = useCallback(
    opt => {
      const productId = opt?.id ?? null;
      if (filters.product !== productId) {
        dispatch(productInventoryLogSlice.actions.setFilters({ ...filters, product: productId }));
      }
    },
    [dispatch, filters],
  );

  const handleResetAll = useCallback(() => {
    dispatch(productInventoryLogSlice.actions.setFilters({ product: null, action: null, user: null }));
  }, [dispatch]);

  const toolbarContent = (
    <FilterBar
      search={
        <Box sx={{ minWidth: 240, maxWidth: 360, flex: 1 }}>
          <ProductAutocomplete fullWidth size="small" value={filters?.product} onChange={handleProductChange} />
        </Box>
      }
      onResetAll={handleResetAll}
    />
  );

  return (
    <EntityGrid
      basePath="product-inventory-logs"
      fieldOverrides={fieldOverrides}
      extraParams={extraParams}
      options={{
        searchPlaceholder: t('warehouse.product.searchLog'),
        persistKey: 'productInventoryLogGrid',
        infiniteScroll: true,
        toolbarContent,
        columnVisibility,
        countNoun: t('warehouse.product.logEntriesCountNoun'),
      }}
    />
  );
}
